/**
 * SITE CHROME — the header above `#app` and the footer below it, and the one
 * place that decides which routes have them.
 *
 * ── WHY THIS EXISTS AT ALL ──────────────────────────────────────────────────
 * It was assembled from two halves that did not meet. The footer was mounted
 * globally by `main.ts` as a sibling of `#app` and hidden on `/play`. The
 * header was built as `createHeader()` and called by exactly one screen — the
 * landing page. The result was a site where `/`, and only `/`, had a brand, a
 * nav and a skip link, while `/legal/terms`, `/faq`, `/news`, `/account` and
 * the 404 had a footer hanging under a page with no way back to anything.
 * Meanwhile `/admin` — which draws its own console shell, its own nav and its
 * own brand — got the public marketing footer, payment logos and all.
 *
 * So both halves live here, both are siblings of `#app`, and both obey one
 * predicate: `hasSiteChrome(path)` in `routes.ts`. Adding a route now decides
 * its chrome by being a document or an application surface, not by whether
 * somebody remembered to call `createHeader`.
 *
 * ── WHY SIBLINGS OF `#app` AND NOT INSIDE IT ────────────────────────────────
 * Every screen owns `#app` and replaces its contents wholesale — the core auth
 * screens assign `root.innerHTML`, and the match hands the element to Pixi.
 * Chrome rendered inside it would be destroyed by the next navigation. As
 * siblings they survive routing, which is also why `styles/app.css` makes the
 * body a flex column: `#app` grows, and the chrome sits above and below it
 * instead of on top of it.
 *
 * ── HIDDEN, NOT UNMOUNTED ───────────────────────────────────────────────────
 * Entering `/play` sets `hidden` on both hosts rather than tearing them down.
 * The footer holds two live subscriptions and two network caches; churning
 * them on every navigation into and out of a match buys nothing and loses the
 * language control's focus restoration. `[hidden]` is `display:none`, which
 * takes them out of layout, out of the tab order and out of the accessibility
 * tree — the three things that matter.
 */
import { authStore, type AuthUser } from '../../core/state/auth-store';
import { subscribeI18n } from '../i18n';
import { mountFooter, type FooterHandle } from './footer';
import { createHeader } from './header';
import { hasSiteChrome, normalisePath } from './routes';
import {
  EMPTY_SITE_INFO,
  loadSiteInfo,
  peekSiteInfo,
  type GameSiteInfo,
} from './site-info';

export const HEADER_HOST_ID = 'site-header-host';
export const FOOTER_HOST_ID = 'site-footer-host';

export interface SiteChromeOptions {
  /** The routed application root. Chrome is inserted around it. */
  readonly app: HTMLElement;
  /** End the session. Wired to the header's sign-out button. */
  readonly onSignOut?: () => void;
}

export interface SiteChromeHandle {
  readonly header: HTMLElement;
  readonly footer: HTMLElement;
  /** Call on every navigation. Cheap when nothing changed. */
  update(path: string): void;
  /** True when the current path is showing chrome. For tests and the router. */
  readonly visible: boolean;
  destroy(): void;
}

/**
 * Mount the header and the footer around `app`, and return the handle the
 * router drives.
 *
 * The header renders synchronously from whatever is cached, then again when
 * `/site-info`, the language or the session changes — the same contract the
 * footer already had, so neither is ever the reason a page is blank.
 */
export function mountSiteChrome(options: SiteChromeOptions): SiteChromeHandle {
  const { app } = options;
  const doc = app.ownerDocument;
  const parent = app.parentNode ?? doc.body;

  const headerHost = doc.createElement('div');
  headerHost.id = HEADER_HOST_ID;
  // STICKY LIVES HERE, not on the <header>: the host is a flow child of the
  // scrolling body, so `position:sticky` on it pins to the viewport top; the
  // header inside is only as tall as itself, so sticky there sticks to nothing.
  headerHost.className = 'sticky top-0 z-40';
  parent.insertBefore(headerHost, app);

  const footerHost = doc.createElement('div');
  footerHost.id = FOOTER_HOST_ID;
  // `insertBefore(node, app.nextSibling)` rather than `appendChild`: the app
  // root is not necessarily the last child of its parent, and a footer that
  // jumped to the end of `<body>` would sit under whatever else lives there.
  parent.insertBefore(footerHost, app.nextSibling);

  let destroyed = false;
  let path = normalisePath(doc.defaultView?.location?.pathname ?? '/');
  let siteInfo: GameSiteInfo = peekSiteInfo() ?? EMPTY_SITE_INFO;
  let user: AuthUser | null = authStore.get().user;
  let footer: FooterHandle | null = null;

  const renderHeader = (): void => {
    if (destroyed) return;
    const header = createHeader({
      user,
      siteInfo,
      currentPath: path,
      ...(options.onSignOut === undefined ? {} : { onSignOut: options.onSignOut }),
    });
    headerHost.replaceChildren(header);
  };

  const applyVisibility = (): void => {
    const show = hasSiteChrome(path);
    headerHost.hidden = !show;
    footerHost.hidden = !show;
  };

  // Both fire immediately, so the chrome is fully drawn before this returns.
  const offI18n = subscribeI18n(() => renderHeader());
  const offAuth = authStore.subscribe((state) => {
    const next = state.user;
    // Identity, not reference: the store re-publishes on every status change
    // (`authenticating`, `error`), and rebuilding the header on each one would
    // pull focus off the sign-out button the user is in the middle of pressing.
    if (next?.id === user?.id && (next === null) === (user === null)) return;
    user = next;
    renderHeader();
  });

  footer = mountFooter(footerHost);
  applyVisibility();

  void loadSiteInfo().then((loaded) => {
    if (destroyed) return;
    siteInfo = loaded;
    renderHeader();
  });

  return {
    get header(): HTMLElement {
      return headerHost;
    },
    get footer(): HTMLElement {
      return footerHost;
    },
    get visible(): boolean {
      return !headerHost.hidden;
    },
    update(next: string): void {
      const normalised = normalisePath(next);
      if (normalised === path) return;
      path = normalised;
      applyVisibility();
      // `aria-current` is per-path, so the header is rebuilt even when it is
      // hidden — entering `/play` and coming back must not leave the nav
      // claiming the player is still on the page they left.
      renderHeader();
    },
    destroy(): void {
      destroyed = true;
      offI18n();
      offAuth();
      footer?.destroy();
      footer = null;
      headerHost.remove();
      footerHost.remove();
    },
  };
}
